/**
 * searchIndex — búsqueda del header (ARS-21): número de regulador o nombre
 * de calle. Todo en cliente sobre datos estáticos (`santaColomaIntersections`
 * y `streets.ts`), sin geocoder externo.
 */
import { santaColomaIntersections } from '../data/santa-coloma';
import { streets, type Street } from '../data/streets';
import { toDisplayId } from './canonicalId';
import type { IntersectionDefinition } from '../types/intersection';

export const MAX_RESULTS = 8;

/** Minúsculas y sin diacríticos: "Galícia" → "galicia". */
export function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export interface RegulatorHit { kind: 'regulator'; intersection: IntersectionDefinition; displayId: string }
export interface StreetHit { kind: 'street'; street: Street }
export type SearchHit = RegulatorHit | StreetHit;

const DIGITS_ONLY = /^\d{1,4}$/;

/** "29" → 029 exacto primero; "07" → todos los que contengan "07" en su ID. */
export function searchRegulators(query: string, limit = MAX_RESULTS): RegulatorHit[] {
  const q = query.trim();
  if (!DIGITS_ONLY.test(q)) return [];
  const exact = Number(q);
  const hits = santaColomaIntersections
    .map(ix => ({ kind: 'regulator' as const, intersection: ix, displayId: toDisplayId(ix.cabinet.logicalId) }))
    .filter(h => h.displayId.includes(q) || h.intersection.cabinet.logicalId === exact)
    .sort((a, b) => {
      const ae = a.intersection.cabinet.logicalId === exact ? 0 : 1;
      const be = b.intersection.cabinet.logicalId === exact ? 0 : 1;
      return ae - be || a.intersection.cabinet.logicalId - b.intersection.cabinet.logicalId;
    });
  return hits.slice(0, limit);
}

/** Subcadena sobre el nombre normalizado, en cualquier posición: "pallaresa"
 * → "Avinguda Pallaresa". Las que empiezan por la consulta van primero. */
export function searchStreets(query: string, limit = MAX_RESULTS): StreetHit[] {
  const q = normalize(query);
  if (q.length < 2) return [];
  const hits = streets
    .map(street => ({ street, name: normalize(street.name) }))
    .filter(x => x.name.includes(q))
    .sort((a, b) => {
      const aw = a.name.split(/\s+/).some(w => w.startsWith(q)) ? 0 : 1;
      const bw = b.name.split(/\s+/).some(w => w.startsWith(q)) ? 0 : 1;
      return aw - bw || a.name.localeCompare(b.name);
    });
  return hits.slice(0, limit).map(x => ({ kind: 'street' as const, street: x.street }));
}

export function search(query: string): SearchHit[] {
  return [...searchRegulators(query), ...searchStreets(query)];
}
